import * as THREE from 'three';
import Stats from 'stats.js';
import { InitialDialog } from '../ui/InitialDialog';
import { GameScene } from '../scenes/GameScene';
import { InputController } from '../controllers/InputController';
import { NetworkManager } from './NetworkManager';
import { GUI } from 'lil-gui';
import { FullscreenManager } from './FullscreenManager';
import { Debug, LogCategory, LogLevel } from '../../../shared/Debug';

const gui = new GUI();


// Function to check if debug mode is enabled via URL fragment
function isDebugModeEnabled(): boolean {
  return window.location.hash === '#debug';
}

// Show GUI if debug mode is enabled
if (isDebugModeEnabled()) {
  gui.show();
  Debug.info('Debug mode enabled - GUI visible', LogCategory.GENERAL);
}
else {
  gui.hide();
  Debug.info('Debug mode disabled - GUI hidden', LogCategory.GENERAL);
}

// Add listener to check for hash changes
window.addEventListener('hashchange', () => {
  if (isDebugModeEnabled()) {
    gui.show();
    Debug.info('Debug mode enabled - GUI visible', LogCategory.GENERAL);
  } else {
    gui.hide();
    Debug.info('Debug mode disabled - GUI hidden', LogCategory.GENERAL);
  }
});


export class GameManager {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private gameScene: GameScene | null = null;
  private initialDialog: InitialDialog | null = null;
  private inputController: InputController | null = null;
  private networkManager: NetworkManager | null = null;
  private isGameStarted = false;
  private stats: Stats;
  private threeContainer: HTMLElement | null = null;
  private fullscreenManager: FullscreenManager;
  
  constructor() {
    // Initialize Three.js components
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    
    // lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 2.5);
    this.scene.add(ambientLight);

    // Add directional light for shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 2);
    directionalLight.position.set(1, 20, 10);
    directionalLight.castShadow = true;
    // Configure shadow properties
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 50;
    directionalLight.shadow.camera.left = -18;
    directionalLight.shadow.camera.right = 18;
    directionalLight.shadow.camera.top = 12;
    directionalLight.shadow.camera.bottom = -12;
    directionalLight.shadow.bias = -0.0001;
    this.scene.add(directionalLight);
    
    // Set up renderer
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate = true;  // Enable auto update for shadows
    this.renderer.shadowMap.needsUpdate = true; // Force an update of the shadow map
    
    // Initialize performance monitor
    this.stats = new Stats();
    this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
    document.body.appendChild(this.stats.dom);
    
    // Style the stats container
    this.stats.dom.style.position = 'absolute';
    this.stats.dom.style.top = '0px';
    this.stats.dom.style.left = '0px';
    this.stats.dom.style.cursor = 'pointer';

    if (isDebugModeEnabled()) {
      this.stats.dom.style.display = 'block';
    }
    else {
      this.stats.dom.style.display = 'none';
    }
    
    // Add click event to toggle between panels
    this.stats.dom.addEventListener('click', () => {
      // Cycle through panels: 0 (FPS) -> 1 (MS) -> 2 (MB) -> 0 (FPS)
      const currentPanel = parseInt(this.stats.dom.children[0].classList[0].slice(-1));
      const nextPanel = (currentPanel + 1) % 3;
      this.stats.showPanel(nextPanel);
    });
    
    // Add keyboard shortcut to toggle stats visibility ('S' key)
    window.addEventListener('keydown', (event) => {
      if (isDebugModeEnabled() && (event.key === 's' || event.key === 'S')) {
        this.stats.dom.style.display = this.stats.dom.style.display === 'none' ? 'block' : 'none';
      }
    });
    
    // Get the container element
    this.threeContainer = document.getElementById('three-container');
    if (!this.threeContainer) {
      throw new Error('Could not find three-container element');
    }
    
    // Initialize fullscreen manager
    this.fullscreenManager = new FullscreenManager(this.threeContainer);
    
    // Add renderer to container
    this.threeContainer.appendChild(this.renderer.domElement);
    
    // Set up camera position
    this.camera.position.set(0, 10, 20);
    this.camera.lookAt(0, 0, 0);
    
    // Handle window resize
    window.addEventListener('resize', this.onWindowResize.bind(this));

    // Initialize fullscreen on mobile
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      this.fullscreenManager.enterFullscreen();
    }
  }
  
  private assetsLoaded: boolean = false;
  private loadingProgress: number = 0;
  
  /**
   * Initialize the game and show the loading dialog
   * This allows the user to start the game immediately while assets load in the background
   */
  public async init(): Promise<void> {
    // Create the initial dialog right away
    this.initialDialog = new InitialDialog(this.startGame.bind(this));
    this.initialDialog.show();
  }

  
  private startGame(options: {
    avatarIndex: number;
    gameMode: 'open' | 'private';
    gameCode?: string;
    playerName: string;
    isHomeTeam?: boolean;
  }): void {
    // Initialize the network manager
    this.networkManager = new NetworkManager();
    
    
    // Set up room connected callback
    this.networkManager.onRoomConnected((roomId: string) => {
      Debug.info('Room connected with ID: ' + roomId, LogCategory.GENERAL);

      if (this.gameScene) {
        this.gameScene.dispose();
        this.gameScene = null;
      }
      
      // Hide the initial dialog and show the game scene
      if (this.initialDialog) {
        this.initialDialog.hide();
      }
      if (this.threeContainer) {
        this.threeContainer.style.display = 'block';
      }
      
      // Create the game scene
      this.gameScene = new GameScene(
        this.scene, 
        this.camera, 
        this.networkManager,
        this.renderer
      );
            
      // Create input controller
      this.inputController = new InputController(this.camera, this.gameScene);
      
      // Start the animation loop if not already started
      if (!this.isGameStarted) {
        this.isGameStarted = true;
        this.animate();
      }
    });
    
    // Connect to the game server
    this.networkManager.connect(options).catch(error => {
      Debug.error('Failed to connect to game server: ' + error, LogCategory.GENERAL);
      
      // Show error message to user
      if (this.initialDialog) {
        this.initialDialog.showError('Failed to connect to game server. Please try again.');
      }
    });
  }
  
  private onWindowResize(): void {
    // Update camera aspect ratio
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    
    // Update renderer size
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    // Handle fullscreen resize
    this.fullscreenManager.handleResize();
  }
  
  private lastFrameTime: number = 0;
  
  private animate(currentTime: number = 0): void {
    requestAnimationFrame(this.animate.bind(this));
    
    // Calculate delta time in seconds (clamped to avoid large jumps)
    const deltaTime = Math.min(0.1, (currentTime - this.lastFrameTime) / 1000);
    this.lastFrameTime = currentTime;
    
    // Begin stats measurement
    this.stats.begin();
    
    // Update game logic if game is started
    if (this.isGameStarted && this.gameScene) {
      this.gameScene.update(deltaTime);
    }
    
    // Update input controller
    if (this.inputController) {
      this.inputController.update(deltaTime);
    }
    
    // Render the scene
    this.renderer.render(this.scene, this.camera);
    
    // End stats measurement
    this.stats.end();
  }
} 